import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import { fetchTonight } from './api.js'
import { teamById, teamFor } from './colors.js'
import { fetchHistory, fetchSeasons, historyEndpoint, validSeason } from './history.js'
import { teamNames } from './historyFormat.js'
import { LOADING, READY, UNREAD } from './poll.js'
import { headModel, kickerOf } from './seasonModel.js'
import { useNow } from '../useNow.js'

// What the season views share while they are on screen. P6-329c (GN-2c).
//
// ═══════════════════════════════════════════════════════════════════════════
// ONE PROVIDER PER VISIT TO THE SEASON VIEWS, NO MODULE-LEVEL CACHE
// ═══════════════════════════════════════════════════════════════════════════
//
// The board's hook says it plainly (`poll.js`): *one reader per panel and no module-level cache, the trade `movie.js`,
// `music.js`, `watch.js` and `directory.js` all made: a promise cache saves a request and buys shared mutable state
// every test has to reset.* The season views keep that trade and keep the saving, by scoping the cache to a
// COMPONENT: `SeasonShell` is the parent of every season view (react-router keeps a parent mounted while its children
// change), so the list of seasons, the teams table and every file already read live exactly as long as somebody is
// moving between Players, Leaders, Standings, Teams and the pages under them, and are gone when they leave. Nothing
// outlives the visit, nothing needs resetting between tests, and opening a player from the list does not read the
// index again.
//
// ═══════════════════════════════════════════════════════════════════════════
// A FAILURE IS NEVER CACHED AND NEVER SAYS WHY
// ═══════════════════════════════════════════════════════════════════════════
//
// Only an answer that checked out is kept. A read that failed is `UNREAD` for the component that asked, and the next
// component to ask the same thing asks again, so one hiccup is not a page that stays empty until a reload. And, as on
// the board (`api.js`), the status is not carried: an unreadable feed is the header and no sentence (HP5), and a
// distinction nothing renders is one somebody will one day render by accident. The operator has the status in the
// network tab and `functions/api/hoops/_failure.js` logs the reason.
//
// ═══════════════════════════════════════════════════════════════════════════
// THE VIEWS WAIT FOR BOTH THE SEASONS AND THE TEAMS BEFORE THEY DRAW
// ═══════════════════════════════════════════════════════════════════════════
//
// The seasons say which season the switcher shows, and the board's teams table is where every colour and every logo
// comes from (`colors.js`): a page that drew before it arrived would draw neutral washes and then repaint them, on every
// navigation. Both are one small request each, made together, and both are `settled` once they have ANSWERED, whether or
// not the answer was good: a deployment whose board is down still gets its history, in neutral, with the logos as
// the design's discs (`Marks.jsx`).

const HistoryContext = createContext(null)

const NO_TEAMS = Object.freeze([])
const UNAVAILABLE = Object.freeze({ state: UNREAD, data: null })

/** One read, made when this mounts and never again. `null` until it has answered. */
function useOnce(read) {
  const [answer, setAnswer] = useState(null)
  useEffect(() => {
    let cancelled = false
    read().then((result) => {
      if (!cancelled) setAnswer(result)
    })
    return () => {
      cancelled = true
    }
    // The read is a closure over nothing that changes: it is made once per mount.
  }, [])
  return answer
}

/** Ask once for `url` and share the answer: two components that want the same file wait on one request. */
function load(shared, url, read) {
  const pending = shared.inflight.get(url)
  if (pending) return pending
  const promise = read().then((answer) => {
    shared.inflight.delete(url)
    if (answer.ok) shared.cache.set(url, answer.data)
    return answer
  })
  shared.inflight.set(url, promise)
  return promise
}

export function HistoryProvider({ children, timeZone }) {
  const [shared] = useState(() => ({ cache: new Map(), inflight: new Map() }))
  const seasonsAnswer = useOnce(() => fetchSeasons())
  const boardAnswer = useOnce(() => fetchTonight())

  const value = useMemo(
    () => ({
      shared,
      seasons:
        seasonsAnswer === null
          ? { state: LOADING, list: [] }
          : seasonsAnswer.ok
            ? { state: READY, list: seasonsAnswer.seasons }
            : { state: UNREAD, list: [] },
      teams: boardAnswer?.ok ? boardAnswer.teams : NO_TEAMS,
      settled: seasonsAnswer !== null && boardAnswer !== null,
      timeZone,
    }),
    [shared, seasonsAnswer, boardAnswer, timeZone],
  )

  return <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>
}

function useStore() {
  const value = useContext(HistoryContext)
  if (value === null) throw new Error('the season views are rendered outside a HistoryProvider')
  return value
}

/** `{ settled }`: have the seasons and the teams both answered? */
export function useSettled() {
  return useStore().settled
}

/**
 * One of a season's files: `{ state, data }`, `data` being the file as the bot published it.
 *
 * `loading` until it has answered, `ready` with the file, `unread` when the answer was a failure (an absent file, an
 * unreachable bucket, a file that was not the one asked for: the page does not tell them apart). A bad season or id,
 * or a `null` one, is `unread` at once and asks nothing, which is how a view says "do not ask for this yet".
 */
export function useHistoryResource(kind, season, id = null) {
  const { shared } = useStore()
  const url = historyEndpoint(kind, season, id)
  const [snapshot, setSnapshot] = useState({ url: null, state: LOADING, data: null })

  useEffect(() => {
    if (url === null || shared.cache.has(url)) return undefined
    let cancelled = false
    load(shared, url, () => fetchHistory(kind, season, id)).then((answer) => {
      if (!cancelled) {
        setSnapshot({ url, state: answer.ok ? READY : UNREAD, data: answer.ok ? answer.data : null })
      }
    })
    return () => {
      cancelled = true
    }
    // `url` IS the identity of the read: the kind, the season and the id all go into it.
  }, [url])

  if (url === null) return UNAVAILABLE
  if (shared.cache.has(url)) return { state: READY, data: shared.cache.get(url) }
  return snapshot.url === url ? snapshot : { state: LOADING, data: null }
}

/**
 * The seasons the bot has published and the one this view shows.
 *
 * `selected` is the entry for `?season=` when that is a published season, and the NEWEST published season when it is
 * absent, is not a season, or names one the bot has not published: a link never lands on an empty page because it
 * was typed wrong. `choose` writes the season into the address with `replace`, because a season is a refinement of
 * the page you are on and not another page for Back to walk through.
 */
export function useSeasonChoice() {
  const { seasons } = useStore()
  const [params, setParams] = useSearchParams()
  const location = useLocation()
  const requested = validSeason(params.get('season'))
  const selected = seasons.list.find((entry) => entry.season === requested) ?? seasons.list[0] ?? null
  const choose = useCallback(
    (season) =>
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous)
          next.set('season', season)
          return next
        },
        // The state rides along: it is what says where Back goes (`useBack.js`), and it is the same visit.
        { replace: true, state: location.state },
      ),
    [setParams, location.state],
  )
  return { state: seasons.state, list: seasons.list, selected, choose }
}

/**
 * The season the address names, when it names a PUBLISHED one, or `null`: what the tab bar's links carry so that choosing
 * a different tab keeps the season. An address that names nothing (the newest, by default) carries nothing, so a member
 * who never chose follows the newest season wherever they go, and one whose address says `?season=2023-24` (well-formed,
 * unpublished) is not sent on carrying a value the next page would also refuse.
 */
export function useCarriedSeason() {
  const { seasons } = useStore()
  const [params] = useSearchParams()
  const requested = validSeason(params.get('season'))
  return requested !== null && seasons.list.some((entry) => entry.season === requested) ? requested : null
}

/**
 * The season a player's numbers are compared with: the one before it (older), or, for the oldest, the one after.
 * With a single season there is none. The list is newest first.
 */
export function comparisonSeason(list, season) {
  const index = list.findIndex((entry) => entry.season === season)
  if (index < 0) return null
  return list[index + 1] ?? list[index - 1] ?? null
}

/**
 * The board's teams table and what the views ask of it: a team's entry by id or tricode, its full name, and its city
 * and nickname. Every one has an answer without the table (the standings row's own name, then the tricode), so an
 * unreadable board costs the colours and the logos and not a name.
 */
export function useTeams() {
  const { teams } = useStore()
  return useMemo(
    () => ({
      table: teams,
      byId: (id) => teamById(teams, id),
      byAbbr: (abbr) => teamFor(teams, abbr),
      nameOf: (teamId, abbr, fallback) => {
        const entry = teamById(teams, teamId)
        if (entry) return `${entry.city ?? ''} ${entry.name ?? ''}`.trim() || (fallback ?? abbr ?? '')
        return fallback ?? abbr ?? ''
      },
      namesOf: (teamId, abbr, full) => teamNames(full ?? abbr, teamById(teams, teamId)),
    }),
    [teams],
  )
}

/**
 * What is said above a view, as `seasonModel.js::headModel` writes it: the kicker and the line that dates the file.
 *
 * `ready` is whether the view has the file whose date it is to print. Until it has, the head is the kicker alone (the
 * season control stays on screen while a different season loads, and a line reading "no date on this" for a file that
 * is merely on its way would be a claim the page does not know is true).
 */
export function useHead({ season, entry, asOf, source, ready }) {
  const { timeZone } = useStore()
  const nowMs = useNow()
  if (!ready) return { kicker: kickerOf(season, entry), age: null, stale: false, staleNote: null, finished: true }
  return headModel({ season, entry, asOf, source, nowMs, timeZone })
}
